import { FC, useState } from 'react';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import { useHsCodesRedux } from 'hooks/useHsCodesRedux';
import { useManualHsCodesRedux } from 'hooks/useManualHsCodesRedux';
import { IHsCodeItem } from 'redux/reducers/hscodes/types';
import { HsCodeField } from './HsCodeField';

type HsCodeSearchMode = 'search-by-name' | 'search-by-code' | 'manual' | 'custom';

interface HsCodeSelectorProps {
	value?: IHsCodeItem | null;
	onChange?: (value: IHsCodeItem | null) => void;
	label?: string;
}

export const HsCodeSelector: FC<HsCodeSelectorProps> = ({ value, onChange, label = 'HS Code' }) => {
	const [hsCodeSearchMode, setHsCodeSearchMode] = useState<HsCodeSearchMode>('search-by-code');
	const [hsCodeInput, setHsCodeInput] = useState<string>('');
	const [hsCodeValue, setHsCodeValue] = useState<IHsCodeItem | null>(value || null);
	const [manualInputValue, setManualInputValue] = useState<string>('');

	const { options: hsCodeOptions, loading: hsCodeLoading } = useHsCodesRedux(
		hsCodeInput,
		hsCodeSearchMode === 'search-by-name' ? 'search-by-name' : 'search-by-code',
	);

	const {
		options: manualHsCodeOptions,
		loading: manualHsCodeLoading,
		hsCodeStack,
		finalHsCode,
		currentPath,
		currentSelection,
		selectHsCode,
		goBack,
		reset: resetManualSelection,
		loadOptions,
		canGoBack,
		isComplete,
	} = useManualHsCodesRedux();

	const handleSearchModeChange = (newMode: HsCodeSearchMode) => {
		setHsCodeSearchMode(newMode);
		setHsCodeInput('');
		setHsCodeValue(null);
		setManualInputValue('');
		onChange?.(null);
		resetManualSelection();
	};

	const handleHsCodeChange = (newValue: IHsCodeItem | null) => {
		setHsCodeValue(newValue);
		onChange?.(newValue);
	};

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			<Box>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					HS Code Search Mode:
				</Typography>
				<RadioGroup
					value={hsCodeSearchMode}
					onChange={(event) => handleSearchModeChange(event.target.value as HsCodeSearchMode)}
					sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}
				>
					<FormControlLabel
						value="search-by-name"
						control={<Radio size="small" />}
						label="Search by name"
					/>
					<FormControlLabel
						value="search-by-code"
						control={<Radio size="small" />}
						label="Search by code"
					/>
					<FormControlLabel value="manual" control={<Radio size="small" />} label="Manual" />
					<FormControlLabel value="custom" control={<Radio size="small" />} label="Custom" />
				</RadioGroup>
			</Box>

			<HsCodeField
				mode={hsCodeSearchMode}
				label={label}
				hsCodeInput={hsCodeInput}
				hsCodeValue={hsCodeValue}
				hsCodeOptions={hsCodeOptions}
				hsCodeLoading={hsCodeLoading}
				manualInputValue={manualInputValue}
				manualHsCodeOptions={manualHsCodeOptions}
				manualHsCodeLoading={manualHsCodeLoading}
				hsCodeStack={hsCodeStack}
				finalHsCode={finalHsCode}
				currentPath={currentPath}
				currentSelection={currentSelection}
				canGoBack={canGoBack}
				isComplete={isComplete}
				onInputChange={setHsCodeInput}
				onValueChange={handleHsCodeChange}
				onManualInputChange={setManualInputValue}
				onManualValueChange={(newValue) => {
					if (newValue) {
						selectHsCode(newValue);
						setManualInputValue(`${newValue.code} - ${newValue.description}`);
						if (newValue.code.length >= 7) {
							onChange?.(newValue);
						}
					}
				}}
				onGoBack={() => {
					goBack();
					if (hsCodeStack.length > 0) {
						const previousItem = hsCodeStack[hsCodeStack.length - 1];
						setManualInputValue(`${previousItem.code} - ${previousItem.description}`);
						onChange?.(null);
					} else {
						setManualInputValue('');
						onChange?.(null);
					}
				}}
				onReset={() => {
					resetManualSelection();
					setManualInputValue('');
					onChange?.(null);
				}}
				onFocus={() => {
					if (!currentSelection && hsCodeStack.length === 0 && !finalHsCode) {
						loadOptions(0);
					}
				}}
				onChange={onChange}
			/>
		</Box>
	);
};
